import { Comment, DraftAnchor } from './types';

// Marcador para o DOM da própria camada de comentários — usado para nunca
// ancorar/capturar cliques na nossa própria UI.
export const UI_ATTR = 'data-comments-ui';

export function isInsideCommentsUI(el: Element | null): boolean {
  let n: Element | null = el;
  while (n) {
    if (n.getAttribute && n.getAttribute(UI_ATTR) != null) return true;
    n = n.parentElement;
  }
  return false;
}

function cssEscape(s: string): string {
  const C = (window as any).CSS;
  if (C && typeof C.escape === 'function') return C.escape(s);
  return s.replace(/["\\]/g, '\\$&');
}

// ids gerados por libs (radix, react useId, hashes) não são estáveis entre sessões.
function isStableId(id: string): boolean {
  if (!id || id.length >= 64) return false;
  if (/^:r/i.test(id)) return false; // React.useId
  if (/^radix-/i.test(id)) return false;
  if (/[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id)) return false; // uuid-like
  return true;
}

function identityOf(el: Element): string | null {
  const testid = el.getAttribute('data-testid');
  if (testid) return `[data-testid="${cssEscape(testid)}"]`;
  if (el.id && isStableId(el.id)) return `#${cssEscape(el.id)}`;
  return null;
}

/** Sobe da folha até o ancestral-ou-próprio mais próximo com identidade estável. */
export function pickAnchorElement(start: Element): Element {
  let el: Element | null = start;
  for (let i = 0; i < 8 && el; i++) {
    if (identityOf(el)) return el;
    el = el.parentElement;
  }
  return start;
}

/**
 * Seletor para um elemento: data-testid → id → caminho estrutural ANCORADO
 * (num ancestral com identidade estável ou em `body`). Se não conseguir ancorar
 * dentro do limite de profundidade, retorna '' — aí o pin usa só as coordenadas
 * de fallback, em vez de um seletor solto que casaria o nó errado (listas/cards).
 */
export function buildSelector(el: Element): string {
  const direct = identityOf(el);
  if (direct) return direct;

  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;
  while (node && node.nodeType === 1 && depth < 12) {
    const current: Element = node; // ref estável por iteração (evita no-loop-func)
    if (current.tagName.toLowerCase() === 'body') {
      parts.unshift('body');
      break;
    }
    const id = identityOf(current);
    if (id) {
      parts.unshift(id); // ancestral estável → âncora
      break;
    }
    let part = current.tagName.toLowerCase();
    const parent: Element | null = current.parentElement;
    if (parent) {
      const sameTag = Array.from(parent.children).filter((c) => c.tagName === current.tagName);
      if (sameTag.length > 1) part += `:nth-of-type(${sameTag.indexOf(current) + 1})`;
    }
    parts.unshift(part);
    node = current.parentElement;
    depth += 1;
  }

  const root = parts[0] || '';
  const anchored = root === 'body' || root.startsWith('[data-testid') || root.startsWith('#');
  return anchored ? parts.join(' > ') : '';
}

/** Captura âncora + fallback no instante do clique (coordenadas em viewport). */
export function captureAnchor(clientX: number, clientY: number): DraftAnchor {
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  const docW = document.documentElement.scrollWidth || window.innerWidth;

  const pageX = clientX + scrollX;
  const pageY = clientY + scrollY;

  const fallback: Pick<DraftAnchor, 'fallback_x' | 'fallback_y' | 'viewportX' | 'viewportY'> = {
    fallback_x: docW > 0 ? pageX / docW : 0, // 0–1 na largura
    fallback_y: pageY, // px no eixo Y (relativo à página)
    viewportX: clientX,
    viewportY: clientY,
  };

  const target = document.elementFromPoint(clientX, clientY);
  if (!target || isInsideCommentsUI(target)) {
    return { anchor_selector: null, anchor_offset_x: null, anchor_offset_y: null, ...fallback };
  }

  const el = pickAnchorElement(target);
  const rect = el.getBoundingClientRect();
  const selector = buildSelector(el);
  const offsetX = rect.width > 0 ? (clientX - rect.left) / rect.width : 0.5;
  const offsetY = rect.height > 0 ? (clientY - rect.top) / rect.height : 0.5;

  return {
    anchor_selector: selector || null,
    anchor_offset_x: clamp01(offsetX),
    anchor_offset_y: clamp01(offsetY),
    ...fallback,
  };
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

export type PinKind = 'anchored' | 'fallback' | 'general';
export interface PinPosition {
  kind: PinKind;
  x: number; // viewport
  y: number; // viewport
}

/** Resolve a posição de render de um comentário (sempre retorna algo — nunca esconde). */
export function positionFor(c: Comment): PinPosition {
  // 1. Âncora primária: seletor resolve e o elemento é visível.
  if (c.anchor_selector) {
    let el: Element | null = null;
    try {
      el = document.querySelector(c.anchor_selector);
    } catch (_) {
      el = null;
    }
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        return {
          kind: 'anchored',
          x: r.left + (c.anchor_offset_x ?? 0.5) * r.width,
          y: r.top + (c.anchor_offset_y ?? 0.5) * r.height,
        };
      }
    }
  }

  // 2. Fallback: coordenada de página → viewport (se ainda fizer sentido).
  const docW = document.documentElement.scrollWidth || window.innerWidth;
  const docH = document.documentElement.scrollHeight || window.innerHeight;
  const pageX = c.fallback_x * docW;
  const pageY = c.fallback_y;
  const withinPage = c.fallback_x >= 0 && c.fallback_x <= 1 && pageY <= docH + 40;
  if (withinPage) {
    return { kind: 'fallback', x: pageX - window.scrollX, y: pageY - window.scrollY };
  }

  // 3. Página mudou demais → comentário geral da tela (agrupado num canto).
  return { kind: 'general', x: 0, y: 0 };
}
